import { useEffect, useState } from 'react'
import ResellerShell from './ResellerShell'
import { tournamentApi } from '../../services/api'
import ProviderCredentialsForm from '../../components/ProviderCredentialsForm'
import MapPicker from '../../components/MapPicker'
import CoursePhotoModal from '../../components/CoursePhotoModal'
import { useLanguage } from '../../context/LanguageContext'

// Tee-sheet providers — mirror of buildProviders() from setup/TeeSheetSetup.jsx
// (kept inline here so we don't have to plumb the i18n t() function deep
// into the reseller-scoped page).
// taglineKey / nameKey point at existing i18n entries (English fallback kept
// inline so the screen still reads in English if a key is missing). Product
// names like "Tee-On" / "ChronoGolf" / "TeeTimeCentral" stay untranslated;
// only the descriptive "Generic Tee Sheet" name carries a nameKey.
const PROVIDERS = [
  { key: 'integolf',      name: 'Generic Tee Sheet',           nameKey: 'sp_generic_tee_sheet', tagline: "Use InteGolf's built-in tee sheet (recommended for new courses)", taglineKey: 'sp_tagline_integolf_native', icon: '⛳', available: true },
  { key: 'integolf-main', name: 'InteGolf Main API',           nameKey: 'sp_integolf_main_api', tagline: "Pick from InteGolf's consumer-platform catalog (preview — golf scope may need to be enabled on the API key)", taglineKey: 'sp_tagline_integolf_main', icon: '🌍', available: true, livePicker: true },
  { key: 'teeon-classic', name: 'Tee-On (Classic)',            tagline: 'Connect an existing Tee-On account',                              taglineKey: 'sp_tagline_teeon_classic', icon: '🏌', available: true },
  { key: 'teeon-gn',      name: 'Tee-On (Golf North)',         tagline: 'Modern Tee-On API for Golf North courses',                        taglineKey: 'sp_provider_teeon_gn_tag', icon: '🏌', available: true },
  { key: 'chronogolf',    name: 'ChronoGolf',                  tagline: 'Lightspeed Golf / ChronoGolf integration',                        taglineKey: 'sp_provider_chronogolf_tag', icon: '⏱', available: true },
  { key: 'ttc',           name: 'TeeTimeCentral (TTC)',        tagline: "Pick from TTC's catalog using InteGolf's partner account",         taglineKey: 'sp_tagline_ttc', icon: '📅', available: true },
  { key: 'teetime-com',   name: 'tee-time.com (Libeo)',        tagline: 'Pull live tee times from the Libeo / VIPGolf marketplace',         taglineKey: 'sp_tagline_teetime_com', icon: '🌐', available: true },
  // Non-integration ("request-mode") course: no tee sheet, we don't book it.
  // Reseller sets a fixed price + the customer picks a preferred time window;
  // the tee time is confirmed by hand after payment.
  { key: 'none',          name: 'No tee sheet',                nameKey: 'sp_provider_none', tagline: "We don't book this course — capture a preferred time window and your price, then confirm the tee time by hand.", taglineKey: 'sp_provider_none_tag', icon: '📝', available: true },
  { key: 'club-prophet',  name: 'Club Prophet',                tagline: 'Coming soon — partner API access required',                        taglineKey: 'sp_tagline_club_prophet', icon: '💰', available: false },
]

/**
 * Connected Courses — Phase 3.
 *
 * Two sections:
 *   - "Connected" — courses the reseller has personally attached, with WhoPaysCourse toggle
 *   - "Available" — courses in the platform the reseller hasn't attached yet, search + attach
 *
 * Per Q9 (open marketplace), the package builder catalog in Phase 5/6 will
 * still surface every course in radius regardless of attachment. Attachment
 * here is specifically about (a) declaring "I have a relationship with this
 * course" and (b) opting in to "we (reseller) will pay this course directly"
 * via WhoPaysCourse=reseller.
 */
export default function ConnectedCourses() {
  const { t } = useLanguage()
  const [data, setData] = useState({ attached: [], available: [], total: 0 })
  const [loading, setLoading] = useState(true)
  const [q, setQ] = useState('')
  const [provinceFilter, setProvinceFilter] = useState('')
  const [error, setError] = useState(null)
  const [showAdd, setShowAdd] = useState(false)
  const [editingCoursesId, setEditingCoursesId] = useState(null)
  const [photoCourse, setPhotoCourse] = useState(null) // course whose photo modal is open
  // Connected-courses sub-filter — 'all' (default), 'integolf' (platform-
  // managed integrations like TTC / ChronoGolf where we use partner
  // credentials), or 'yours' (reseller-owned integrations like Tee-On
  // with their own login).
  const [tab, setTab] = useState('all')
  // Connected-list search / filter / paging (client-side — the full attached
  // list is already loaded). 154+ courses were unusable as one long scroll.
  const [connSearch, setConnSearch] = useState('')
  const [connProvince, setConnProvince] = useState('')
  const [connProvider, setConnProvider] = useState('')
  const [connNeedsAttn, setConnNeedsAttn] = useState(false)
  const [rcResellerName, setRcResellerName] = useState('') // "Reseller Courses" tab: filter by contributing reseller
  const [connPage, setConnPage] = useState(1)
  const CONN_PAGE_SIZE = 25
  // Reset to page 1 whenever the filter set changes.
  useEffect(() => { setConnPage(1) }, [connSearch, connProvince, connProvider, connNeedsAttn, tab])

  function reload() {
    setLoading(true); setError(null)
    tournamentApi.get('/portal/reseller/courses', { params: { q, limit: 50 } })
      .then(r => setData(r.data))
      .catch(err => setError(err?.response?.data?.message || err.message))
      .finally(() => setLoading(false))
  }
  useEffect(() => { reload() }, [q])

  async function attach(courseRow) {
    try {
      // TTC-virtual rows: use connect-course endpoint (creates courses row +
      // integration row in one shot). Rate suggestion was pre-computed
      // server-side and arrives in courseRow._suggestedRate.
      if (courseRow._virtualSource === 'ttc') {
        await tournamentApi.post('/portal/reseller/courses/connect', {
          provider: 'ttc',
          credentials: {
            crsid: courseRow._ttcCourseCode,
            teesheet: courseRow._ttcTeesheet,
            courseName: courseRow.CourseName,
            rateCode: courseRow._suggestedRate?.rateCode || null,
            rateName: courseRow._suggestedRate?.rateName || null,
          },
          course: {
            name: courseRow.CourseName,
            city: courseRow.City,
            latitude: courseRow.Latitude, longitude: courseRow.Longitude,
          },
          whoPaysCourse: 'reseller',
        })
      } else {
        await tournamentApi.post('/portal/reseller/courses', { coursesId: courseRow.Courses_ID, whoPaysCourse: 'reseller' })
      }
      reload()
    } catch (err) { alert(err?.response?.data?.message || err.message) }
  }
  async function setWhoPays(coursesId, value) {
    try {
      await tournamentApi.patch(`/portal/reseller/courses/${coursesId}`, { whoPaysCourse: value })
      setData(d => ({
        ...d,
        attached: d.attached.map(c => c.Courses_ID === coursesId ? { ...c, WhoPaysCourse: value } : c),
      }))
    } catch (err) { alert(err?.response?.data?.message || err.message); reload() }
  }
  async function setNotes(coursesId, value) {
    try {
      await tournamentApi.patch(`/portal/reseller/courses/${coursesId}`, { notes: value })
    } catch (err) { alert(err?.response?.data?.message || err.message) }
  }
  // Per-reseller override of the course's advance booking window (days). Blank
  // clears it (falls back to the course/provider default). Drives CTQ deferred
  // golf: custom-trip rounds further out than this auto-book when the window opens.
  async function setBookingWindow(coursesId, value) {
    const clean = value === '' ? null : Math.max(0, Math.round(Number(value) || 0))
    try {
      await tournamentApi.patch(`/portal/reseller/courses/${coursesId}`, { bookingWindowDays: clean })
      setData(d => ({
        ...d,
        attached: d.attached.map(c => c.Courses_ID === coursesId ? { ...c, BookingWindowDays: clean } : c),
      }))
    } catch (err) { alert(err?.response?.data?.message || err.message); reload() }
  }
  // Request-only (concierge) course: can't be booked via API, so the customer
  // requests a time + pays the reseller's fixed price, and the reseller confirms
  // the tee time by hand after. 'live' clears the request price/note.
  async function setBookingMode(coursesId, value) {
    try {
      await tournamentApi.patch(`/portal/reseller/courses/${coursesId}`, { bookingMode: value })
      setData(d => ({
        ...d,
        attached: d.attached.map(c => c.Courses_ID === coursesId
          ? { ...c, BookingMode: value, ...(value === 'live' ? { RequestPrice: null, RequestNote: null } : {}) }
          : c),
      }))
    } catch (err) { alert(err?.response?.data?.message || err.message); reload() }
  }
  async function setRequestPrice(coursesId, value) {
    const clean = value === '' ? null : Math.max(0, Math.round(Number(value) * 100) / 100 || 0)
    try {
      await tournamentApi.patch(`/portal/reseller/courses/${coursesId}`, { requestPrice: clean })
      setData(d => ({
        ...d,
        attached: d.attached.map(c => c.Courses_ID === coursesId ? { ...c, RequestPrice: clean } : c),
      }))
    } catch (err) { alert(err?.response?.data?.message || err.message); reload() }
  }
  async function setCardOnFile(coursesId, value) {
    try {
      await tournamentApi.patch(`/portal/reseller/courses/${coursesId}/card-on-file`, { cardOnFile: value })
      setData(d => ({ ...d, attached: d.attached.map(c => c.Courses_ID === coursesId ? { ...c, CardOnFile: value ? 1 : 0 } : c) }))
    } catch (err) { alert(err?.response?.data?.message || err.message) }
  }
  async function setRequestNote(coursesId, value) {
    try {
      await tournamentApi.patch(`/portal/reseller/courses/${coursesId}`, { requestNote: value })
    } catch (err) { alert(err?.response?.data?.message || err.message) }
  }
  // A course photo just filled in (CoursePhoto did the upload + PATCH). Reflect it.
  function setCoursePhoto(coursesId, url) {
    setData(d => ({
      ...d,
      attached: d.attached.map(c => c.Courses_ID === coursesId ? { ...c, imageUrl: url } : c),
    }))
  }
  async function detach(coursesId, name) {
    if (!confirm(t('sp_detach_confirm', 'Remove {name} from your connected courses?\nYou can re-attach it later.', { name }))) return
    try {
      await tournamentApi.delete(`/portal/reseller/courses/${coursesId}`)
      reload()
    } catch (err) { alert(err?.response?.data?.message || err.message) }
  }

  return (
    <ResellerShell title={t('sp_connected_courses_title', 'Connected courses')}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, gap: 24 }}>
        <p style={{ color: '#64748b', maxWidth: 600, marginTop: 0, marginBottom: 0, fontSize: 14, lineHeight: 1.5 }}>
          {t('sp_connected_courses_intro', 'Attach courses you have a direct relationship with — set up the tee-sheet integration AND choose WhoPaysCourse. Courses you don\'t attach are still usable in your packages but treated as "InteGolf-direct" (we always pay).')}
        </p>
        <button onClick={() => setShowAdd(true)} style={primaryBtn}>{t('sp_add_course_btn', '+ Add Course')}</button>
      </div>

      <PreferredProviderCard />

      {error && <div style={errorBox}>{error}</div>}

      {showAdd && (
        <AddCourseModal
          onClose={() => setShowAdd(false)}
          onSaved={() => { setShowAdd(false); reload() }}
        />
      )}
      {editingCoursesId && (
        <AddCourseModal
          editingCoursesId={editingCoursesId}
          onClose={() => setEditingCoursesId(null)}
          onSaved={() => { setEditingCoursesId(null); reload() }}
        />
      )}
      {photoCourse && (
        <CoursePhotoModal
          courseId={photoCourse.Courses_ID}
          courseName={photoCourse.CourseName}
          city={photoCourse.City}
          imageUrl={photoCourse.imageUrl || ''}
          imagesUrl={`/portal/reseller/courses/${photoCourse.Courses_ID}/images`}
          uploadUrl="/portal/reseller/uploads/image"
          uploadField="image"
          saveUrl={`/portal/reseller/courses/${photoCourse.Courses_ID}/image`}
          t={t}
          onClose={() => setPhotoCourse(null)}
          onSaved={({ imageUrl }) => { setCoursePhoto(photoCourse.Courses_ID, imageUrl); setPhotoCourse(null) }}
        />
      )}

      {/* CONNECTED section */}
      {(() => {
        // Tab classification mirrors the lock flag returned by the API:
        // platform-managed integrations (whoPaysCourseLocked) are the
        // ones InteGolf provides credentials for — TTC, ChronoGolf, etc.
        // Anything else is a reseller-owned integration.
        const integolfCourses = data.attached.filter(c => c.whoPaysCourseLocked)
        const yourCourses     = data.attached.filter(c => !c.whoPaysCourseLocked)
        const tabbedCourses =
          tab === 'integolf' ? integolfCourses :
          tab === 'yours'    ? yourCourses :
                               data.attached

        // Client-side search + filter + paging over the tab's courses.
        const provinceOf = c => c.Province || (c.City || '').split(',').map(s => s.trim()).slice(-1)[0] || ''
        const needsAttn = c => !!c.isZeroPriceFlagged || (!c.RateCode && !INLINE_PRICING_PROVIDERS.has(c.IntegrationProvider))
        const connSearchLc = connSearch.trim().toLowerCase()
        const connProvinces = [...new Set(data.attached.map(provinceOf).filter(Boolean))].sort((a, b) => a.localeCompare(b))
        const connProviders = [...new Set(data.attached.map(c => c.IntegrationProvider).filter(Boolean))].sort()
        const filteredConn = tabbedCourses.filter(c => {
          if (connSearchLc && !`${c.CourseName} ${c.City || ''}`.toLowerCase().includes(connSearchLc)) return false
          if (connProvince && provinceOf(c) !== connProvince) return false
          if (connProvider && (c.IntegrationProvider || '') !== connProvider) return false
          if (connNeedsAttn && !needsAttn(c)) return false
          return true
        })
        const connTotalPages = Math.max(1, Math.ceil(filteredConn.length / CONN_PAGE_SIZE))
        const connPageSafe = Math.min(connPage, connTotalPages)
        const pageCourses = filteredConn.slice((connPageSafe - 1) * CONN_PAGE_SIZE, connPageSafe * CONN_PAGE_SIZE)
        const connFiltering = !!(connSearchLc || connProvince || connProvider || connNeedsAttn)

        // "Reseller Courses" — marketplace of courses OTHER resellers have added
        // and shared. Distinct data set (data.resellerCourses), so render its own
        // list with attribution + a contributing-reseller filter, then Add.
        if (tab === 'reseller') {
          const rc = (data.resellerCourses || []).filter(c =>
            (!rcResellerName || (c.AddedByResellerName || '') === rcResellerName) &&
            (!connSearchLc || `${c.CourseName} ${c.City || ''}`.toLowerCase().includes(connSearchLc)))
          return (
            <>
              <h2 style={h2}>{t('sp_your_connected_courses_count', 'Your connected courses ({count})', { count: data.attached.length })}</h2>
              <div style={{ display: 'flex', gap: 4, marginBottom: 12, borderBottom: '1px solid #e2e8f0', flexWrap: 'wrap' }}>
                <TabPill active={false} onClick={() => setTab('all')}      label={t('all', 'All')}                  count={data.attached.length} />
                <TabPill active={false} onClick={() => setTab('integolf')} label={t('sp_tab_integolf_integrations', 'InteGolf integrations')} count={integolfCourses.length} />
                <TabPill active={false} onClick={() => setTab('yours')}    label={t('sp_tab_your_integrations', 'Your integrations')}     count={yourCourses.length} />
                <TabPill active={true}  onClick={() => setTab('reseller')} label={t('sp_tab_reseller_courses', 'Reseller Courses')} count={(data.resellerCourses || []).length} />
              </div>
              <p style={{ color: '#64748b', margin: '0 0 12px', fontSize: 13 }}>
                {t('sp_rc_intro', 'Courses other resellers have added and shared. Add one to use it in your own packages.')}
              </p>
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <input type="text" value={connSearch} onChange={e => setConnSearch(e.target.value)}
                  placeholder={t('sp_conn_search_ph', 'Search course or city…')} style={{ ...inp, flex: '1 1 220px', minWidth: 180, maxWidth: 320 }} />
                <select value={rcResellerName} onChange={e => setRcResellerName(e.target.value)} style={{ ...inp, width: 'auto' }}>
                  <option value="">{t('sp_rc_all_resellers', 'All resellers')}</option>
                  {(data.contributors || []).map(r => <option key={r.resellerAccountId} value={r.name}>{r.name}</option>)}
                </select>
                {(rcResellerName || connSearch) && (
                  <button onClick={() => { setRcResellerName(''); setConnSearch('') }} style={{ ...secondaryBtn, fontSize: 12 }}>{t('sp_conn_clear', 'Clear')}</button>
                )}
              </div>
              {rc.length === 0
                ? <div style={empty}>{t('sp_rc_empty', 'No shared courses from other resellers match.')}</div>
                : (
                  <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', marginBottom: 32 }}>
                    <table style={tbl}>
                      <thead><tr style={tr}>
                        <th style={th}>{t('sp_col_course', 'Course')}</th>
                        <th style={th}>{t('sp_rc_col_added_by', 'Added by')}</th>
                        <th style={th}>{t('sp_col_integration', 'Integration')}</th>
                        <th style={th}></th>
                      </tr></thead>
                      <tbody>
                        {rc.map(c => (
                          <tr key={c.Courses_ID} style={tr}>
                            <td style={td}><strong>{c.CourseName}</strong>{c.City ? <div style={{ fontSize: 12, color: '#64748b' }}>{c.City}</div> : null}</td>
                            <td style={td}>{c.AddedByResellerName || '—'}</td>
                            <td style={{ ...td, fontSize: 12, color: '#475569' }}>{c.IntegrationProvider || '—'}</td>
                            <td style={{ ...td, textAlign: 'right' }}>
                              {c.attachedByMe
                                ? <span style={{ fontSize: 12, color: '#059669', fontWeight: 600 }}>{t('sp_rc_added', '✓ Added')}</span>
                                : <button style={secondaryBtn} onClick={() => attach(c)}>{t('sp_rc_add', 'Add to my courses')}</button>}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
            </>
          )
        }

        return (
          <>
            <h2 style={h2}>{t('sp_your_connected_courses_count', 'Your connected courses ({count})', { count: data.attached.length })}</h2>

            {data.attached.length > 0 && (
              <div style={{ display: 'flex', gap: 4, marginBottom: 12, borderBottom: '1px solid #e2e8f0' }}>
                <TabPill active={tab === 'all'}      onClick={() => setTab('all')}      label={t('all', 'All')}                  count={data.attached.length} />
                <TabPill active={tab === 'integolf'} onClick={() => setTab('integolf')} label={t('sp_tab_integolf_integrations', 'InteGolf integrations')} count={integolfCourses.length} />
                <TabPill active={tab === 'yours'}    onClick={() => setTab('yours')}    label={t('sp_tab_your_integrations', 'Your integrations')}     count={yourCourses.length} />
                <TabPill active={tab === 'reseller'} onClick={() => setTab('reseller')} label={t('sp_tab_reseller_courses', 'Reseller Courses')} count={(data.resellerCourses || []).length} />
              </div>
            )}

            {/* Search + filters for the connected list. */}
            {tabbedCourses.length > 0 && (
              <div style={{ display: 'flex', gap: 8, marginBottom: 12, flexWrap: 'wrap', alignItems: 'center' }}>
                <input type="text" value={connSearch} onChange={e => setConnSearch(e.target.value)}
                  placeholder={t('sp_conn_search_ph', 'Search course or city…')}
                  style={{ ...inp, flex: '1 1 220px', minWidth: 180, maxWidth: 320 }} />
                <select value={connProvince} onChange={e => setConnProvince(e.target.value)} style={{ ...inp, width: 'auto' }}>
                  <option value="">{t('sp_conn_all_provinces', 'All provinces')}</option>
                  {connProvinces.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <select value={connProvider} onChange={e => setConnProvider(e.target.value)} style={{ ...inp, width: 'auto' }}>
                  <option value="">{t('sp_conn_all_integrations', 'All integrations')}</option>
                  {connProviders.map(p => <option key={p} value={p}>{p}</option>)}
                </select>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, fontSize: 13, color: '#475569', cursor: 'pointer' }}>
                  <input type="checkbox" checked={connNeedsAttn} onChange={e => setConnNeedsAttn(e.target.checked)} />
                  {t('sp_conn_needs_attention', 'Needs attention (no rate / $0)')}
                </label>
                {connFiltering && (
                  <button onClick={() => { setConnSearch(''); setConnProvince(''); setConnProvider(''); setConnNeedsAttn(false) }}
                    style={{ ...secondaryBtn, fontSize: 12 }}>{t('sp_conn_clear', 'Clear')}</button>
                )}
              </div>
            )}

            {data.attached.length === 0
              ? <div style={empty}>{t('sp_no_connected_courses', 'No connected courses yet. Attach one from the list below.')}</div>
              : tabbedCourses.length === 0
                ? <div style={empty}>{tab === 'integolf' ? t('sp_no_integolf_managed', 'No InteGolf-managed integrations on your account yet.') : t('sp_no_reseller_managed', 'No reseller-managed integrations on your account yet.')}</div>
                : filteredConn.length === 0
                  ? <div style={empty}>{t('sp_conn_no_matches', 'No connected courses match your filters.')}</div>
                : (
          <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden', marginBottom: 32 }}>
            <table style={tbl}>
              <thead>
                <tr style={tr}>
                  <th style={th}>{t('sp_col_course', 'Course')}</th>
                  <th style={th}>{t('sp_col_integration', 'Integration')}</th>
                  <th style={th}>{t('sp_col_rate', 'Rate')}</th>
                  <th style={th}>{t('sp_col_who_pays_course', 'Who pays the course')}</th>
                  <th style={th}>{t('sp_col_green_fee', 'Green fee')}</th>
                  <th style={th}></th>
                </tr>
              </thead>
              <tbody>
                {pageCourses.map(c => (
                  <tr key={c.Courses_ID} style={tr}>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start', marginBottom: 4 }}>
                        <CoursePhotoThumb course={c} onOpen={() => setPhotoCourse(c)} t={t} />
                        <div style={{ minWidth: 0 }}>
                          <strong>{c.CourseName}</strong>
                          <div style={{ fontSize: 12, color: '#64748b' }}>{c.City || ''}</div>
                        </div>
                      </div>
                      <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4 }}
                        title={t('sp_booking_window_tip', 'How many days out this course opens tee-time booking. Custom-trip rounds further ahead auto-book when the window opens. Blank = no limit / use the provider default.')}>
                        <span>{t('sp_booking_window', 'Booking window')}:</span>
                        <input type="number" min="0" max="730"
                          defaultValue={c.BookingWindowDays ?? ''}
                          placeholder={c.CourseWindowDays != null ? String(c.CourseWindowDays) : t('sp_none_short', 'any')}
                          onBlur={(e) => { const nv = e.target.value; const cur = c.BookingWindowDays ?? ''; if (String(nv) !== String(cur)) setBookingWindow(c.Courses_ID, nv) }}
                          style={{ width: 52, padding: '2px 6px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 11.5 }} />
                        <span>{t('sp_days', 'days')}</span>
                      </div>
                      {/* Request-only (concierge) course: no API booking — the
                          customer requests a time + pays, reseller confirms after. */}
                      <div style={{ fontSize: 11.5, color: '#94a3b8', marginTop: 4, display: 'flex', alignItems: 'center', gap: 4, flexWrap: 'wrap' }}
                        title={t('sp_booking_mode_tip', "Live = booked instantly via the course's tee-sheet. Request-only = the customer requests a time and pays your fixed price; you confirm the tee time with the course by hand (for courses that don't allow direct booking, e.g. Cabot).")}>
                        <span>{t('sp_booking_mode', 'Booking')}:</span>
                        <select value={c.BookingMode || 'live'} onChange={(e) => setBookingMode(c.Courses_ID, e.target.value)}
                          style={{ padding: '2px 6px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 11.5 }}>
                          <option value="live">{t('sp_booking_mode_live', 'Live (book via API)')}</option>
                          <option value="request">{t('sp_booking_mode_request', 'Request only')}</option>
                        </select>
                        {c.BookingMode === 'request' && (
                          <>
                            <span>{t('sp_request_price', 'Price/golfer')} $</span>
                            <input type="number" min="0" step="0.01"
                              defaultValue={c.RequestPrice ?? ''} placeholder="0.00"
                              onBlur={(e) => { const nv = e.target.value; const cur = c.RequestPrice ?? ''; if (String(nv) !== String(cur)) setRequestPrice(c.Courses_ID, nv) }}
                              style={{ width: 66, padding: '2px 6px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 11.5 }} />
                            <input type="text" maxLength={120}
                              defaultValue={c.RequestNote ?? ''} placeholder={t('sp_request_note_ph', 'Note (e.g. confirmed in 48h)')}
                              onBlur={(e) => { const nv = e.target.value; const cur = c.RequestNote ?? ''; if (nv !== cur) setRequestNote(c.Courses_ID, nv) }}
                              style={{ width: 200, padding: '2px 6px', border: '1px solid #e2e8f0', borderRadius: 4, fontSize: 11.5 }} />
                          </>
                        )}
                      </div>
                    </td>
                    <td style={td}><IntegrationBadge has={c.HasIntegration} provider={c.IntegrationProvider} /></td>
                    <td style={td}><RateBadge rateCode={c.RateCode} rateName={c.RateName} provider={c.IntegrationProvider} onPick={() => setEditingCoursesId(c.Courses_ID)} /></td>
                    <td style={td}>
                      {c.whoPaysCourseLocked ? (
                        <div
                          title={t('sp_integolf_pays_locked_tip', "InteGolf manages this integration's credentials, so InteGolf always pays the course.")}
                          style={{
                            display: 'inline-flex', alignItems: 'center', gap: 6,
                            padding: '6px 10px', fontSize: 13, color: '#0f172a',
                            background: '#f1f5f9', border: '1px solid #e2e8f0', borderRadius: 6,
                          }}
                        >
                          🔒 <span><strong>{t('sp_integolf_pays_course', 'InteGolf pays course')}</strong></span>
                        </div>
                      ) : (
                        <select
                          value={c.WhoPaysCourse}
                          onChange={(e) => setWhoPays(c.Courses_ID, e.target.value)}
                          style={selectStyle}
                        >
                          <option value="reseller">{t('sp_reseller_pays_no_fee', 'Reseller pays course (no fee from us)')}</option>
                          <option value="integolf">{t('sp_integolf_pays_course', 'InteGolf pays course')}</option>
                        </select>
                      )}
                      {/* Card-on-file: only meaningful when the reseller pays the
                          course. Drives the "card on file" badge on Course Payments. */}
                      {!c.whoPaysCourseLocked && c.WhoPaysCourse === 'reseller' && (
                        <label title={t('sp_card_on_file_tip', 'Check this if the course keeps your credit card on file (they charge it automatically). It still appears on Course Payments so you can confirm and record each charge.')}
                          style={{ display: 'flex', alignItems: 'center', gap: 5, marginTop: 6, fontSize: 11.5, color: c.CardOnFile ? '#0369a1' : '#94a3b8', cursor: 'pointer' }}>
                          <input type="checkbox" checked={!!c.CardOnFile} onChange={(e) => setCardOnFile(c.Courses_ID, e.target.checked)} style={{ width: 13, height: 13 }} />
                          {t('sp_card_on_file_label', 'Card on file')}
                        </label>
                      )}
                    </td>
                    <td style={td}>
                      {c.isZeroPriceFlagged ? (
                        <span
                          onClick={() => setEditingCoursesId(c.Courses_ID)}
                          title={t('sp_green_fee_zero_tip', 'This course returned $0 on a live search, so its rounds are hidden. Fix the Rate, or set a flat fee.')}
                          style={{ color: '#b45309', fontWeight: 600, fontSize: 12.5, cursor: 'pointer' }}
                        >⚠ {t('sp_green_fee_zero', '$0 — needs a fee')}</span>
                      ) : c.GreenFeeFrom != null ? (
                        <span title={t('sp_green_fee_tip', 'Cheapest cached green fee (base, before your markup). Updates as the tee-time cache refreshes.')}>
                          <span style={{ color: '#64748b', fontSize: 12 }}>{t('sp_green_fee_from', 'from')} </span>
                          <strong style={{ color: '#0f172a' }}>${c.GreenFeeFrom}</strong>
                        </span>
                      ) : (
                        <span title={t('sp_green_fee_none_tip', 'Not cached yet — fills as the tee-time cache warms or a customer searches this course.')} style={{ color: '#cbd5e1' }}>—</span>
                      )}
                    </td>
                    <td style={td}>
                      <button onClick={() => setEditingCoursesId(c.Courses_ID)} style={{ ...secondaryBtn, marginRight: 6 }}>{t('edit', 'Edit')}</button>
                      <button onClick={() => detach(c.Courses_ID, c.CourseName)} style={{ ...secondaryBtn, color: '#dc2626', borderColor: '#fecaca' }}>{t('sp_remove', 'Remove')}</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
            {/* Result count + pager */}
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 12, padding: '10px 14px', borderTop: '1px solid #f1f5f9', flexWrap: 'wrap' }}>
              <span style={{ fontSize: 12.5, color: '#64748b' }}>
                {t('sp_conn_showing', 'Showing {from}–{to} of {total}', {
                  from: (connPageSafe - 1) * CONN_PAGE_SIZE + 1,
                  to: Math.min(connPageSafe * CONN_PAGE_SIZE, filteredConn.length),
                  total: filteredConn.length,
                })}
              </span>
              {connTotalPages > 1 && (
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <button onClick={() => setConnPage(p => Math.max(1, p - 1))} disabled={connPageSafe <= 1}
                    style={{ ...secondaryBtn, opacity: connPageSafe <= 1 ? 0.4 : 1, cursor: connPageSafe <= 1 ? 'default' : 'pointer' }}>
                    &larr; {t('sp_prev', 'Prev')}
                  </button>
                  <span style={{ fontSize: 12.5, color: '#475569' }}>{t('sp_conn_page_of', 'Page {page} of {pages}', { page: connPageSafe, pages: connTotalPages })}</span>
                  <button onClick={() => setConnPage(p => Math.min(connTotalPages, p + 1))} disabled={connPageSafe >= connTotalPages}
                    style={{ ...secondaryBtn, opacity: connPageSafe >= connTotalPages ? 0.4 : 1, cursor: connPageSafe >= connTotalPages ? 'default' : 'pointer' }}>
                    {t('sp_next', 'Next')} &rarr;
                  </button>
                </div>
              )}
            </div>
          </div>
        )
            }
          </>
        )
      })()}

      {/* AVAILABLE section */}
      {(() => {
        // Derive unique province list from the available data + counts so the
        // dropdown options show "(N)" hints and only present provinces that
        // actually exist in the current list.
        const provinces = {}
        for (const c of data.available) {
          const p = c.Province || (c.City || '').split(',').map(s=>s.trim()).slice(-1)[0] || null
          if (p) provinces[p] = (provinces[p] || 0) + 1
        }
        const provinceOptions = Object.entries(provinces).sort((a,b)=>a[0].localeCompare(b[0]))
        const filtered = data.available.filter(c => {
          if (!provinceFilter) return true
          const p = c.Province || (c.City || '').split(',').map(s=>s.trim()).slice(-1)[0] || ''
          return p === provinceFilter
        })
        return (
          <>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 12, gap: 8, flexWrap: 'wrap' }}>
              <h2 style={{ ...h2, margin: 0 }}>
                {t('sp_available_courses_count', 'Available courses ({count})', { count: provinceFilter ? `${filtered.length} ${t('sp_available_courses_of_total', 'of {total}', { total: data.available.length })}` : filtered.length })}
              </h2>
              <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
                <select value={provinceFilter} onChange={e => setProvinceFilter(e.target.value)} style={{ ...inp, width: 220 }}>
                  <option value="">{t('sp_all_provinces_count', 'All provinces ({count})', { count: data.available.length })}</option>
                  {provinceOptions.map(([p, n]) => (
                    <option key={p} value={p}>{p} ({n})</option>
                  ))}
                </select>
                <input
                  type="text"
                  placeholder={t('sp_search_name_or_city', 'Search by name or city…')}
                  value={q}
                  onChange={e => setQ(e.target.value)}
                  style={{ ...inp, width: 280 }}
                />
              </div>
            </div>
          </>
        )
      })()}

      {(() => {
        // Re-compute the filtered list inside the body too so the table renders
        // the same set the count above shows.
        const filtered = data.available.filter(c => {
          if (!provinceFilter) return true
          const p = c.Province || (c.City || '').split(',').map(s=>s.trim()).slice(-1)[0] || ''
          return p === provinceFilter
        })
        return (loading
          ? <div style={empty}>{t('sp_loading_short', 'Loading…')}</div>
          : filtered.length === 0
            ? <div style={empty}>{q ? t('sp_no_matches_for_query', 'No matches for "{query}".', { query: q }) : provinceFilter ? t('sp_no_courses_in_province', 'No courses in {province}.', { province: provinceFilter }) : t('sp_no_more_courses_to_attach', 'No more courses to attach.')}</div>
            : (
              <div style={{ background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, overflow: 'hidden' }}>
                <table style={tbl}>
                  <thead>
                    <tr style={tr}>
                      <th style={th}>{t('sp_col_course', 'Course')}</th>
                      <th style={th}>{t('sp_col_integration', 'Integration')}</th>
                      <th style={th}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {filtered.map((c, i) => (
                    <tr key={c.Courses_ID || `virt-${c._ttcCourseCode || i}`} style={tr}>
                      <td style={td}>
                        <strong>{c.CourseName}</strong>
                        <div style={{ fontSize: 12, color: '#64748b' }}>
                          {c.City ? `📍 ${c.City}` : <span style={{ color: '#dc2626' }}>{t('sp_location_unknown', '📍 Location unknown — pin manually after attach')}</span>}
                        </div>
                        {c._virtualSource === 'ttc' && (
                          <div style={{ fontSize: 11, color: '#3b82f6', marginTop: 2 }}>
                            {t('sp_preloaded_from_ttc', '↗ Pre-loaded from TTC partner catalog · code {code}', { code: c._ttcCourseCode })}
                          </div>
                        )}
                        {c._suggestedRate && (
                          <div style={{ fontSize: 11, marginTop: 4 }}>
                            {c._suggestedRate.confidence === 'high' && (
                              <span style={{ color: '#16a34a' }}>
                                {t('sp_default_rate_high', '⭐ Default rate: {rateName} ({total} total)', { rateName: c._suggestedRate.rateName, total: c._suggestedRate.total })}
                              </span>
                            )}
                            {c._suggestedRate.confidence === 'low' && (
                              <span style={{ color: '#dc2626' }}>
                                {t('sp_no_public_rate_detected', '⚠ No public rate auto-detected ({total} rates) — pick manually after attach', { total: c._suggestedRate.total })}
                              </span>
                            )}
                            {c._suggestedRate.confidence === 'none' && (
                              <span style={{ color: '#dc2626' }}>
                                {t('sp_no_rates_returned', '⚠ No rates returned — may not be bookable')}
                              </span>
                            )}
                          </div>
                        )}
                      </td>
                      <td style={td}><IntegrationBadge has={c.HasIntegration} provider={c.IntegrationProvider} /></td>
                      <td style={td}>
                        <button onClick={() => attach(c)} style={primaryBtn}>{t('sp_attach', '+ Attach')}</button>
                        {c._virtualSource === 'ttc' && (
                          <button
                            onClick={async () => {
                              if (!confirm(t('sp_hide_ttc_confirm', 'Hide "{name}" from your Available list? You can bring it back later if needed.', { name: c.CourseName }))) return
                              try {
                                await tournamentApi.post('/portal/reseller/courses/dismiss-ttc', { ttcCourseCode: c._ttcCourseCode })
                                reload()
                              } catch (err) { alert(err?.response?.data?.message || err.message) }
                            }}
                            title={t('sp_hide_ttc_tip', 'Hide this TTC course from your Available list')}
                            style={{ ...secondaryBtn, marginLeft: 6, color: '#dc2626', borderColor: '#fecaca' }}
                          >{t('hide', 'Hide')}</button>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          ))
      })()}
    </ResellerShell>
  )
}

function TabPill({ active, onClick, label, count }) {
  return (
    <button
      type="button"
      onClick={onClick}
      style={{
        padding: '8px 14px',
        background: 'transparent',
        color: active ? '#0f172a' : '#64748b',
        border: 'none',
        borderBottom: active ? '2px solid #0f172a' : '2px solid transparent',
        marginBottom: -1, // collapse with parent border
        fontSize: 13,
        fontWeight: 600,
        cursor: 'pointer',
        fontFamily: 'inherit',
      }}
    >
      {label} <span style={{ fontWeight: 400, color: '#94a3b8' }}>({count})</span>
    </button>
  )
}

// Course photo thumbnail. Present -> shows the photo. Missing -> a "+ Photo"
// tile that opens the shared CoursePhotoModal (same editor the admin uses).
function CoursePhotoThumb({ course, onOpen, t }) {
  const img = course.imageUrl
  const box = {
    width: 66, height: 48, borderRadius: 6, flexShrink: 0, overflow: 'hidden',
    border: '1px solid #e2e8f0', background: '#f1f5f9',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
  }
  if (img) {
    return <div style={box}><img src={img} alt="" loading="lazy" style={{ width: '100%', height: '100%', objectFit: 'cover' }} /></div>
  }
  return (
    <button type="button" onClick={onOpen} title={t('sp_cc_add_photo', 'Add a course photo')}
      style={{ ...box, cursor: 'pointer', color: '#64748b', fontSize: 11, flexDirection: 'column', gap: 1 }}>
      <span style={{ fontSize: 16, lineHeight: 1 }}>＋</span><span>{t('sp_cc_photo', 'Photo')}</span>
    </button>
  )
}

function IntegrationBadge({ has, provider }) {
  const { t } = useLanguage()
  if (has) return <span style={{ padding: '3px 8px', background: '#dcfce7', color: '#166534', borderRadius: 999, fontSize: 11, fontWeight: 600 }}>{provider || t('sp_badge_bookable', 'Bookable')}</span>
  return <span style={{ padding: '3px 8px', background: '#fef3c7', color: '#92400e', borderRadius: 999, fontSize: 11, fontWeight: 600 }} title={t('sp_badge_manual_tip', 'No active golf-booking integration on this course; bookings will need to be coordinated manually.')}>{t('manual', 'Manual')}</span>
}

// Providers whose pricing is returned inline per tee time — no rate code
// to select. Mirrors INLINE_PRICING_PROVIDERS server-side.
const INLINE_PRICING_PROVIDERS = new Set(['teeon-classic', 'teeon-gn', 'integolf'])

function RateBadge({ rateCode, rateName, provider, onPick }) {
  const { t } = useLanguage()
  // Inline-pricing providers don't expose a rate catalog — show a friendly
  // dash with a tooltip rather than the alarming "NOT SET" badge.
  if (INLINE_PRICING_PROVIDERS.has(provider)) {
    return (
      <span title={t('sp_inline_pricing_tip', 'Pricing is returned inline per tee time — no rate code to select for this provider')}
            style={{ color: '#94a3b8', fontSize: 12 }}>
        — <span style={{ fontSize: 11 }}>{t('sp_inline_pricing', 'inline pricing')}</span>
      </span>
    )
  }
  if (!rateCode) {
    return (
      <button
        type="button"
        onClick={onPick}
        title={t('sp_no_rate_code_tip', 'No rate code configured — bookings will fail. Click to pick one.')}
        style={{
          padding: '3px 8px', background: '#fee2e2', color: '#991b1b',
          borderRadius: 999, fontSize: 11, fontWeight: 600,
          border: 'none', cursor: 'pointer', fontFamily: 'inherit',
        }}
      >
        {t('sp_not_set', '⚠ NOT SET')}
      </button>
    )
  }
  return (
    <span title={t('sp_rate_code_tip', 'Rate code: {rateCode}', { rateCode })}>
      <span style={{ fontSize: 13, color: '#0f172a' }}>{rateName || t('sp_unnamed_rate', '(unnamed rate)')}</span>
    </span>
  )
}

// ─── Add Course modal ───────────────────────────────────────────────────

function AddCourseModal({ onClose, onSaved, editingCoursesId }) {
  const { t } = useLanguage()
  const isEditing = !!editingCoursesId
  const [step, setStep] = useState(isEditing ? 2 : 1) // skip provider picker in edit mode (allow back)
  const [provider, setProvider] = useState(null)
  const [credentials, setCredentials] = useState({})
  const [testResult, setTestResult] = useState(isEditing ? { ok: true, prefilled: true } : null)
  const [course, setCourse] = useState({ name: '', city: '', latitude: null, longitude: null })
  const [whoPaysCourse, setWhoPaysCourse] = useState('reseller')
  const [notes, setNotes] = useState('')
  // Request-mode ('No tee sheet') course: fixed sell price + optional note.
  const [requestPrice, setRequestPrice] = useState('')
  const [requestNote, setRequestNote] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [err, setErr] = useState(null)
  const [loadingDetails, setLoadingDetails] = useState(isEditing)
  const [isPlatformManaged, setIsPlatformManaged] = useState(false)
  // Course-sharing controls (only the reseller who contributed the course):
  // { isOwner, shared, removalDate }.
  const [sharing, setSharing] = useState(null)

  async function saveSharing(next) {
    setSharing(s => ({ ...s, shared: next }))
    try {
      const { data } = await tournamentApi.patch(`/portal/reseller/courses/${editingCoursesId}/sharing`, { shared: next })
      setSharing(s => ({ ...s, shared: data.shared, removalDate: data.sharingRemovalDate }))
    } catch (e) {
      alert(e?.response?.data?.message || e.message)
      setSharing(s => ({ ...s, shared: !next }))
    }
  }

  // Edit mode: fetch existing details + prefill all fields
  useEffect(() => {
    if (!editingCoursesId) return
    setLoadingDetails(true); setErr(null)
    tournamentApi.get(`/portal/reseller/courses/${editingCoursesId}/details`)
      .then(({ data }) => {
        setProvider(data.integration?.provider || 'integolf')
        setCredentials(data.integration?.credentials || {})
        setCourse({
          name: data.course.name || '',
          city: data.course.city || '',
          latitude: data.course.latitude,
          longitude: data.course.longitude,
        })
        setWhoPaysCourse(data.attachment?.whoPaysCourse || 'reseller')
        setNotes(data.attachment?.notes || '')
        setTestResult({ ok: true, prefilled: true })
        setIsPlatformManaged(!!data.isPlatformManaged)
        setSharing(data.sharing || null)
      })
      .catch(e => setErr(e?.response?.data?.message || e.message))
      .finally(() => setLoadingDetails(false))
  }, [editingCoursesId])

  // 'integolf' (our local tee sheet) and 'integolf-main' (other-platform
  // catalog with platform-level keys) skip the credentials form. integolf-main
  // gets a live course picker instead.
  const isLivePicker = provider === 'integolf-main'
  // 'none' = non-integration (request-mode) course: no tee sheet, no credentials.
  const isRequestProvider = provider === 'none'
  const requiresCredentials = provider && provider !== 'integolf' && provider !== 'none' && !isLivePicker
  // Providers where credentials live at InteGolf platform level → InteGolf
  // always pays the course. Mirrors INTEGOLF_PAID_PROVIDERS in the API.
  const INTEGOLF_PAID = new Set(['ttc', 'chronogolf', 'integolf', 'integolf-main'])
  const whoPaysLocked = INTEGOLF_PAID.has(provider)
  // Snap state to 'integolf' whenever a locked provider is picked.
  useEffect(() => { if (whoPaysLocked && whoPaysCourse !== 'integolf') setWhoPaysCourse('integolf') }, [whoPaysLocked])
  // A request-mode course is always reseller-paid (we don't book/settle it).
  useEffect(() => { if (isRequestProvider && whoPaysCourse !== 'reseller') setWhoPaysCourse('reseller') }, [isRequestProvider])

  // For live-picker providers, the user must select a course from the catalog
  // before we can save (we need the externalCourseId).
  const [livePickerCourses, setLivePickerCourses] = useState([])
  const [livePickerLoading, setLivePickerLoading] = useState(false)
  const [livePickerError, setLivePickerError] = useState(null)
  const [selectedExternalCourse, setSelectedExternalCourse] = useState(null)

  // Live-picker keyword search (debounced). The InteGolf Main API supports
  // course discovery via locations:search?type=course&keyword= — works
  // without the courses:* scopes.
  const [pickerKeyword, setPickerKeyword] = useState('')
  useEffect(() => {
    if (!isLivePicker) return
    const kw = pickerKeyword.trim()
    if (kw.length < 2) { setLivePickerCourses([]); setLivePickerError(null); return }
    setLivePickerLoading(true); setLivePickerError(null)
    const t = setTimeout(() => {
      tournamentApi.get('/portal/reseller/integolf-main/courses', { params: { keyword: kw } })
        .then(({ data }) => setLivePickerCourses(data?.courses || []))
        .catch(err => setLivePickerError(err?.response?.data?.message || err.message))
        .finally(() => setLivePickerLoading(false))
    }, 300)
    return () => clearTimeout(t)
  }, [isLivePicker, pickerKeyword])

  const canProceedFromStep2 = course.name
    && (provider === 'integolf'
        || (isLivePicker && selectedExternalCourse)
        || (isRequestProvider && requestPrice !== '' && Number(requestPrice) > 0)
        || (requiresCredentials && testResult?.ok))

  async function save() {
    setSubmitting(true); setErr(null)
    try {
      // For live-picker providers, store the external course id in credentials
      // so the booking adapter can later route requests to the right partner course.
      let creds = null
      if (requiresCredentials) creds = credentials
      else if (isLivePicker && selectedExternalCourse) {
        creds = {
          externalCourseId: selectedExternalCourse.externalId,
          externalCourseName: selectedExternalCourse.name,
        }
      } else if (isLivePicker && credentials?.externalCourseId) {
        // Edit mode for a live-picker course where the user didn't re-pick
        creds = credentials
      }
      const body = {
        provider,
        credentials: creds,
        course: { name: course.name, city: course.city, latitude: course.latitude, longitude: course.longitude },
        whoPaysCourse,
        notes: notes || null,
      }
      // Non-integration course: send the fixed price + optional customer-facing
      // note so the backend attaches it as BookingMode='request' in one step.
      if (isRequestProvider) {
        body.whoPaysCourse = 'reseller'
        body.requestPrice = requestPrice === '' ? null : Number(requestPrice)
        body.requestNote = requestNote || null
      }
      if (isEditing) body.coursesId = editingCoursesId
      await tournamentApi.post('/portal/reseller/courses/connect', body)
      onSaved()
    } catch (e) {
      setErr(e?.response?.data?.message || e.message)
    } finally { setSubmitting(false) }
  }

  return (
    // Backdrop click intentionally does NOT close the modal — multi-step
    // wizard with credentials being typed in, accidental clicks on the
    // dimmed area would discard work. Only the X button, Cancel button,
    // or a successful submit close it. Same for the X to remove the
    // close-on-Escape behavior browsers don't provide by default anyway.
    <div style={overlay}>
      <div style={modalCard} onClick={e => e.stopPropagation()}>
        <div style={modalHeader}>
          <div>
            <h3 style={{ margin: 0, fontSize: 18, color: '#0f172a' }}>{isEditing ? t('sp_edit_course_heading', 'Edit: {name}', { name: course.name || t('sp_col_course', 'course') }) : t('sp_add_a_course', 'Add a course')}</h3>
            <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{t('sp_step_x_of_3', 'Step {step} of 3', { step })}{isEditing ? t('sp_step_editing_suffix', ' · editing') : ''}</div>
          </div>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: '#94a3b8' }}>×</button>
        </div>

        <div style={{ padding: 24, overflowY: 'auto', flex: 1 }}>
          {err && <div style={errorBox}>{err}</div>}
          {loadingDetails && <div style={{ padding: 16, color: '#64748b', fontSize: 13 }}>{t('sp_loading_course_details', 'Loading existing course details…')}</div>}

          {step === 1 && (
            <>
              <p style={{ color: '#64748b', marginTop: 0, marginBottom: 16, fontSize: 14 }}>
                {t('sp_choose_tee_sheet_provider', 'Choose the tee sheet provider for this course.')}
              </p>
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(260px, 1fr))', gap: 10 }}>
                {PROVIDERS.map(p => (
                  <button
                    key={p.key}
                    type="button"
                    disabled={!p.available}
                    onClick={() => { setProvider(p.key); setCredentials({}); setTestResult(null) }}
                    style={{
                      textAlign: 'left', padding: 14, fontFamily: 'inherit',
                      background: '#fff',
                      border: provider === p.key ? '2px solid #00d19f' : '1px solid #e2e8f0',
                      borderRadius: 8,
                      cursor: p.available ? 'pointer' : 'not-allowed',
                      opacity: p.available ? 1 : 0.5,
                    }}
                  >
                    <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 6 }}>
                      <span style={{ fontSize: 20 }}>{p.icon}</span>
                      <strong style={{ fontSize: 14, color: '#0f172a' }}>{p.nameKey ? t(p.nameKey, p.name) : p.name}</strong>
                      {!p.available && <span style={{ fontSize: 10, color: '#94a3b8', letterSpacing: 1 }}>{t('sp_provider_soon', 'SOON')}</span>}
                    </div>
                    <div style={{ fontSize: 12, color: '#64748b', lineHeight: 1.4 }}>{t(p.taglineKey, p.tagline)}</div>
                  </button>
                ))}
              </div>
            </>
          )}

          {step === 2 && (
            <>
              {/* Step 2 layout: PROVIDER FIRST, course basics auto-prefilled
                  from the provider catalog. The user only fills in fields the
                  provider can't supply (lat/lng pin since TTC has no coords). */}

              {requiresCredentials && (
                <>
                  <h4 style={h4}>{t('sp_step_pick_course_at', '1. Pick your course at {provider}', { provider: PROVIDERS.find(p => p.key === provider)?.name })}</h4>
                  {isPlatformManaged ? (
                    <div style={{ padding: 14, background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 6, fontSize: 13, color: '#854d0e', marginBottom: 16 }}>
                      {t('sp_managed_by_integolf_note', '🔒 Managed by InteGolf — this course was set up by an InteGolf admin, who controls the provider and rate code. You can adjust WhoPaysCourse and notes below; contact support to change the rate.')}
                    </div>
                  ) : (
                    <p style={{ color: '#64748b', fontSize: 13, marginTop: 0, marginBottom: 12 }}>
                      {t('sp_pick_catalog_test_connection', 'Pick from the catalog and run "Test Connection". We\'ll auto-fill the course name + location below from what the provider tells us.')}
                    </p>
                  )}
                  {isPlatformManaged ? (
                    <div style={{ padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13 }}>
                      <div><strong>{t('sp_provider_label', 'Provider:')}</strong> {provider}</div>
                      {credentials?.crsid && <div style={{ marginTop: 6 }}><strong>{t('sp_course_code_label', 'Course code:')}</strong> <code>{credentials.crsid}</code></div>}
                      {credentials?.rateName && <div style={{ marginTop: 6 }}><strong>{t('sp_rate_label', 'Rate:')}</strong> {credentials.rateName}</div>}
                      {credentials?.teesheet && <div style={{ marginTop: 6, fontSize: 11, color: '#64748b' }}>{t('sp_backend_label', 'Backend:')} {credentials.teesheet}</div>}
                    </div>
                  ) : (
                    <ProviderCredentialsForm
                      provider={provider}
                      initialCredentials={credentials}
                      onChange={setCredentials}
                      onTestResult={setTestResult}
                      onCourseMetadata={(meta) => {
                        setCourse(c => ({
                          ...c,
                          name: c.name || meta.name || '',
                          city: c.city || [meta.city, meta.state].filter(Boolean).join(', '),
                          latitude: c.latitude != null ? c.latitude : meta.latitude,
                          longitude: c.longitude != null ? c.longitude : meta.longitude,
                        }))
                      }}
                    />
                  )}
                </>
              )}

              {isLivePicker && (
                <>
                  <h4 style={{ ...h4, marginTop: 24 }}>{t('sp_pick_course_from_catalog', 'Pick a course from the InteGolf catalog')}</h4>
                  <Field label={t('sp_search_by_course_name', 'Search by course name')} hint={t('sp_search_catalog_hint', "Type at least 2 characters. We'll search the InteGolf Main catalog live.")}>
                    <input
                      type="text"
                      value={pickerKeyword}
                      onChange={e => setPickerKeyword(e.target.value)}
                      placeholder={t('sp_picker_keyword_placeholder', 'e.g. Lingan, Cabot, Bell Bay…')}
                      style={inp}
                    />
                  </Field>
                  {livePickerError && (
                    <div style={errorBox}>
                      <strong>{livePickerError}</strong>
                      {livePickerError.includes('does not have access') && (
                        <div style={{ marginTop: 8, fontSize: 12, color: '#7f1d1d' }}>
                          {t('sp_locations_search_scope_help', 'The InteGolf Main API key needs the locations:search scope. Ask whoever issued the key to grant it.')}
                        </div>
                      )}
                    </div>
                  )}
                  {livePickerLoading && <div style={{ padding: 16, color: '#64748b', fontSize: 13 }}>{t('sp_searching', 'Searching…')}</div>}
                  {!livePickerLoading && pickerKeyword.trim().length >= 2 && livePickerCourses.length === 0 && !livePickerError && (
                    <div style={{ padding: 12, background: '#fef9c3', border: '1px solid #fde68a', borderRadius: 6, fontSize: 13, color: '#854d0e' }}>
                      {t('sp_no_courses_matching_keyword', 'No courses matching "{keyword}" in the InteGolf Main catalog. Try a different keyword or pick a different provider.', { keyword: pickerKeyword })}
                    </div>
                  )}
                  {!livePickerLoading && livePickerCourses.length > 0 && (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 6, maxHeight: 240, overflowY: 'auto', border: '1px solid #e2e8f0', borderRadius: 6, padding: 6 }}>
                      {livePickerCourses.map(c => (
                        <button
                          key={c.externalId}
                          type="button"
                          onClick={() => {
                            setSelectedExternalCourse(c)
                            // Auto-prefill name (and city if the catalog row had one)
                            setCourse(prev => ({
                              ...prev,
                              name: prev.name || c.name || '',
                              city: prev.city || c.city || (c.label ? c.label.split(',').slice(1).map(s=>s.trim()).join(', ') : ''),
                              latitude: prev.latitude != null ? prev.latitude : c.lat,
                              longitude: prev.longitude != null ? prev.longitude : c.lng,
                            }))
                          }}
                          style={{
                            textAlign: 'left', padding: 10, fontFamily: 'inherit',
                            background: selectedExternalCourse?.externalId === c.externalId ? '#ecfdf5' : '#fff',
                            border: selectedExternalCourse?.externalId === c.externalId ? '1px solid #00d19f' : '1px solid #f1f5f9',
                            borderRadius: 4, cursor: 'pointer',
                          }}
                        >
                          <strong style={{ fontSize: 13, color: '#0f172a' }}>{c.name}</strong>
                          <div style={{ fontSize: 11, color: '#64748b', marginTop: 2 }}>{c.label}</div>
                        </button>
                      ))}
                    </div>
                  )}
                  {selectedExternalCourse && (
                    <div style={{ marginTop: 12, padding: 12, background: '#ecfdf5', border: '1px solid #a7f3d0', borderRadius: 6, fontSize: 13, color: '#065f46' }}>
                      {t('sp_selected_course', '✓ Selected: {name} (catalog id {externalId})', { name: selectedExternalCourse.name, externalId: selectedExternalCourse.externalId })}
                    </div>
                  )}
                </>
              )}

              {/* Course basics — auto-prefilled from provider catalog when
                  available; user can edit + must pin lat/lng on the map. */}
              <h4 style={{ ...h4, marginTop: 24 }}>{requiresCredentials || isLivePicker ? t('sp_step_course_details_numbered', '2. Course details') : t('sp_course_details_heading', 'Course details')}</h4>
              <p style={{ color: '#64748b', fontSize: 13, marginTop: 0, marginBottom: 12 }}>
                {requiresCredentials || isLivePicker
                  ? t('sp_prefilled_from_provider', "Pre-filled from the provider where possible. Adjust the name + city if needed and pin the exact location on the map (most providers don't supply lat/lng).")
                  : t('sp_enter_course_basics', 'Enter the course basics below.')}
              </p>
              <Field label={t('sp_course_name_label', 'Course name')}><input type="text" required style={inp} value={course.name} onChange={e => setCourse(c => ({ ...c, name: e.target.value }))} placeholder={t('sp_course_name_placeholder', 'e.g. Cabot Cliffs')} /></Field>
              <Field label={t('city', 'City')}><input type="text" style={inp} value={course.city || ''} onChange={e => setCourse(c => ({ ...c, city: e.target.value }))} placeholder={t('sp_city_placeholder', 'e.g. Inverness, NS')} /></Field>
              <Field label={t('sp_location_pin_on_map', 'Location (pin on map)')} hint={t('sp_location_hint', 'Used for grouping radius calculations.')}>
                <MapPicker
                  lat={course.latitude}
                  lng={course.longitude}
                  searchQuery={course.name}
                  height={260}
                  onChange={({ lat, lng, address, city }) => setCourse(c => ({ ...c, latitude: lat, longitude: lng, city: c.city || city || address }))}
                />
              </Field>

              {/* Non-integration course: fixed sell price + optional note. No
                  live rate to pull, so the reseller sets the price the customer
                  pays; they pick a preferred time window at booking. */}
              {isRequestProvider && (
                <>
                  <h4 style={{ ...h4, marginTop: 24 }}>{t('sp_request_pricing_heading', 'Request pricing')}</h4>
                  <p style={{ color: '#64748b', fontSize: 13, marginTop: 0, marginBottom: 12 }}>
                    {t('sp_request_pricing_blurb', "We don't book this course. The customer pays this price and picks a preferred time window; you confirm the tee time with the course by hand.")}
                  </p>
                  <Field label={t('sp_request_price', 'Price/golfer')}>
                    <input type="number" min="0" step="0.01" style={{ ...inp, maxWidth: 160 }}
                      value={requestPrice} onChange={e => setRequestPrice(e.target.value)} placeholder="120.00" />
                  </Field>
                  <Field label={t('sp_request_note_optional', 'Customer note (optional)')}>
                    <input type="text" maxLength={500} style={inp}
                      value={requestNote} onChange={e => setRequestNote(e.target.value)}
                      placeholder={t('sp_request_note_ph', 'Note (e.g. confirmed in 48h)')} />
                  </Field>
                </>
              )}
            </>
          )}

          {step === 3 && (
            <>
              <h4 style={h4}>{t('sp_who_pays_course_q', 'Who pays the course?')}</h4>
              {whoPaysLocked ? (
                <div style={{
                  padding: 14, background: '#f0fdfa', border: '1px solid #ccfbf1',
                  borderRadius: 8, fontSize: 13, color: '#0f766e', lineHeight: 1.5,
                }}>
                  {t('sp_who_pays_locked_blurb', "🔒 InteGolf pays the course. Because this integration uses InteGolf's partner credentials with {provider}, we settle with the course directly. The cost is netted out of your Mode A EFT or charged to your Mode B card-on-file.", { provider: PROVIDERS.find(p => p.key === provider)?.name })}
                </div>
              ) : isRequestProvider ? (
                <div style={{
                  padding: 14, background: '#f0fdfa', border: '1px solid #ccfbf1',
                  borderRadius: 8, fontSize: 13, color: '#0f766e', lineHeight: 1.5,
                }}>
                  {t('sp_request_whopays_note', "You pay the course directly — we don't book or settle a non-integration course. The customer pays your price at checkout and you confirm the tee time with the course by hand.")}
                </div>
              ) : (
                <>
                  <p style={{ color: '#64748b', fontSize: 13, marginTop: 0, marginBottom: 12 }}>
                    {t('sp_billing_determines_blurb', "Determines how golf rounds are billed when this course is in a customer's package.")}
                  </p>
                  <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {[
                      { val: 'reseller', title: t('sp_opt_reseller_pays_title', 'I pay the course directly'), desc: t('sp_opt_reseller_pays_desc', 'No charge from InteGolf for these rounds. You handle billing with the course yourself.') },
                      { val: 'integolf', title: t('sp_opt_integolf_pays_title', 'InteGolf pays the course'), desc: t('sp_opt_integolf_pays_desc', 'We pay the course; we charge your card-on-file (Mode B) or net out of your EFT (Mode A) for the cost.') },
                    ].map(opt => (
                      <button key={opt.val} type="button" onClick={() => setWhoPaysCourse(opt.val)} style={{
                        textAlign: 'left', padding: 14, fontFamily: 'inherit',
                        background: '#fff',
                        border: whoPaysCourse === opt.val ? '2px solid #00d19f' : '1px solid #e2e8f0',
                        borderRadius: 8, cursor: 'pointer',
                      }}>
                        <strong style={{ fontSize: 14, color: '#0f172a' }}>{opt.title}</strong>
                        <div style={{ fontSize: 12, color: '#64748b', marginTop: 4 }}>{opt.desc}</div>
                      </button>
                    ))}
                  </div>
                </>
              )}

              <h4 style={{ ...h4, marginTop: 20 }}>{t('sp_internal_notes_optional', 'Internal notes (optional)')}</h4>
              <textarea style={{ ...inp, minHeight: 60 }} value={notes} onChange={e => setNotes(e.target.value)} placeholder={t('sp_internal_notes_placeholder', 'Pro shop contact, special arrangements, etc.')} />

              {/* Course sharing — only the reseller who contributed this course.
                  ON (default) lets other resellers use it; OFF blocks new use now
                  and removes it from current users after a 30-day grace. */}
              {sharing?.isOwner && (
                <div style={{ marginTop: 20, padding: 14, background: sharing.shared ? '#f0fdf4' : '#fffbeb', border: `1px solid ${sharing.shared ? '#bbf7d0' : '#fde68a'}`, borderRadius: 8 }}>
                  <label style={{ display: 'flex', alignItems: 'center', gap: 10, cursor: 'pointer', fontSize: 13, color: '#0f172a' }}>
                    <input type="checkbox" checked={!!sharing.shared} onChange={e => saveSharing(e.target.checked)} />
                    <strong>{t('sp_share_course_toggle', 'Allow other resellers to use this course')}</strong>
                  </label>
                  <div style={{ fontSize: 12, color: '#64748b', marginTop: 6, marginLeft: 26 }}>
                    {sharing.shared
                      ? t('sp_share_course_on_note', 'You added this course, so it\'s available for other resellers to use in their own packages. Turn this off to keep it to yourself.')
                      : t('sp_share_course_off_note', 'Sharing is OFF — no other reseller can newly add it. Any already using it lose access on {date} (30 days after you turned it off). They have been notified. Turn it back on before then to cancel the removal.', { date: sharing.removalDate || '—' })}
                  </div>
                </div>
              )}

              <div style={{ marginTop: 20, padding: 14, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#475569' }}>
                {isRequestProvider ? (
                  <><strong>{t('sp_review_label', 'Review:')}</strong> {course.name}{course.city ? ` (${course.city})` : ''} — {t('sp_review_request_course', 'non-integration course. The customer pays {price}/golfer and picks a preferred time window; you confirm the tee time by hand.', { price: requestPrice !== '' ? `$${Number(requestPrice).toFixed(2)}` : '—' })}</>
                ) : (
                  <><strong>{t('sp_review_label', 'Review:')}</strong> {course.name}{course.city ? ` (${course.city})` : ''} {t('sp_review_via_provider', 'via')} <strong>{PROVIDERS.find(p => p.key === provider)?.name}</strong>. {whoPaysCourse === 'reseller' ? t('sp_review_reseller_pays', 'You pay the course directly.') : t('sp_review_integolf_pays', 'InteGolf pays the course.')}</>
                )}
              </div>
            </>
          )}
        </div>

        <div style={{ padding: '14px 24px', borderTop: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <button onClick={onClose} style={secondaryBtn}>{t('cancel', 'Cancel')}</button>
          <div style={{ display: 'flex', gap: 8 }}>
            {step > 1 && <button onClick={() => setStep(s => s - 1)} style={secondaryBtn}>{t('sp_back_arrow_btn', '← Back')}</button>}
            {step === 1 && <button onClick={() => setStep(2)} disabled={!provider} style={{ ...primaryBtn, opacity: provider ? 1 : 0.5 }}>{t('sp_next_arrow', 'Next →')}</button>}
            {step === 2 && <button onClick={() => setStep(3)} disabled={!canProceedFromStep2} title={!canProceedFromStep2 ? (requiresCredentials && !testResult?.ok ? t('sp_run_test_connection_first', 'Run "Test Connection" successfully first') : t('sp_enter_course_name_tip', 'Enter the course name')) : ''} style={{ ...primaryBtn, opacity: canProceedFromStep2 ? 1 : 0.5 }}>{t('sp_next_arrow', 'Next →')}</button>}
            {step === 3 && <button onClick={save} disabled={submitting} style={{ ...primaryBtn, opacity: submitting ? 0.6 : 1 }}>{submitting ? t('sp_saving', 'Saving…') : (isEditing ? t('sp_save_changes', 'Save changes') : t('sp_add_course_button', 'Add course'))}</button>}
          </div>
        </div>
      </div>
    </div>
  )
}

function Field({ label, hint, children }) {
  return (
    <div style={{ marginBottom: 14 }}>
      <label style={{ display: 'block', fontSize: 12, fontWeight: 600, color: '#475569', marginBottom: 4 }}>{label}</label>
      {children}
      {hint && <div style={{ fontSize: 11, color: '#64748b', marginTop: 4 }}>{hint}</div>}
    </div>
  )
}

const h4 = { margin: '0 0 8px', fontSize: 13, color: '#475569', textTransform: 'uppercase', letterSpacing: 0.5 }
const overlay = { position: 'fixed', inset: 0, background: 'rgba(15,23,42,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: 20 }
const modalCard = { background: '#fff', borderRadius: 12, width: '100%', maxWidth: 720, maxHeight: '90vh', display: 'flex', flexDirection: 'column', boxShadow: '0 20px 50px rgba(0,0,0,0.2)' }
const modalHeader = { padding: '20px 24px', borderBottom: '1px solid #e2e8f0', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }

const h2 = { margin: '0 0 12px', fontSize: 16, color: '#0f172a' }
const errorBox = { padding: 12, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, marginBottom: 16 }
const empty = { padding: 40, textAlign: 'center', color: '#64748b', background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8, marginBottom: 16 }
const tbl = { width: '100%', borderCollapse: 'collapse', fontSize: 14 }
const tr = { borderBottom: '1px solid #f1f5f9' }
const th = { padding: '12px 16px', textAlign: 'left', fontWeight: 600, color: '#64748b', fontSize: 12, letterSpacing: 0.5, textTransform: 'uppercase', background: '#f8fafc' }
const td = { padding: '14px 16px', verticalAlign: 'middle' }
const inp = { padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 14, fontFamily: 'inherit', boxSizing: 'border-box' }
const selectStyle = { padding: '8px 10px', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, fontFamily: 'inherit', background: '#fff', minWidth: 280 }
const primaryBtn = { padding: '8px 14px', background: '#0f172a', color: '#fff', border: 'none', borderRadius: 6, fontSize: 13, fontWeight: 600, cursor: 'pointer' }
const secondaryBtn = { padding: '6px 12px', background: '#fff', color: '#475569', border: '1px solid #e2e8f0', borderRadius: 6, fontSize: 13, cursor: 'pointer' }

/**
 * PreferredProviderCard — reseller picks which tee-sheet provider class to
 * use by default when a course is wired through both InteGolf and Apex.
 *
 * - NULL = no preference. Router falls back to InteGolf-first, then Apex.
 * - 'integolf' = prefer any non-Apex adapter (TTC, TeeOn, ChronoGolf, native).
 * - 'apex'     = prefer teetime-com (Apex/Libeo).
 *
 * Stored on reseller_accounts.PreferredGolfProvider (added in v94). The
 * golf-booking router reads this when resolving the adapter for a search/book.
 * Single-integration courses ignore the preference (only one choice).
 */
function PreferredProviderCard() {
  const { t } = useLanguage()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [value, setValue] = useState('')          // '' | 'integolf' | 'apex'
  const [endpointReady, setEndpointReady] = useState(false)
  const [error, setError] = useState('')
  const [saved, setSaved] = useState(false)
  // Non-integration (request-mode) courses master switch.
  const [offerRequest, setOfferRequest] = useState(false)
  const [isOwner, setIsOwner] = useState(false)
  const [savingOffer, setSavingOffer] = useState(false)
  const [savedOffer, setSavedOffer] = useState(false)

  useEffect(() => {
    let alive = true
    tournamentApi.get('/portal/reseller/account/preferences')
      .then(r => {
        if (!alive) return
        setValue(r.data?.preferredGolfProvider || '')
        setOfferRequest(!!r.data?.offerRequestCourses)
        setIsOwner(!!r.data?.isOwner)
        setEndpointReady(true)
      })
      .catch(() => {
        // Endpoint not deployed yet (404 OR caught by the generic
        // /portal/reseller/* middleware that returns "No course context for
        // tee sheet access"). Either way, silently leave the card at its
        // default state. The dropdown stays disabled until the backend
        // GET succeeds, so the user can't pick something that won't save.
        if (!alive) return
        setEndpointReady(false)
      })
      .finally(() => { if (alive) setLoading(false) })
    return () => { alive = false }
  }, [])

  async function save(next) {
    if (!endpointReady) return
    setSaving(true); setError(''); setSaved(false)
    try {
      await tournamentApi.patch('/portal/reseller/account/preferences', {
        preferredGolfProvider: next || null,
      })
      setValue(next)
      setSaved(true)
      setTimeout(() => setSaved(false), 2500)
    } catch (err) {
      setError(err?.response?.data?.message || err.message)
    } finally { setSaving(false) }
  }

  async function saveOffer(next) {
    if (!endpointReady) return
    setSavingOffer(true); setError(''); setSavedOffer(false)
    try {
      await tournamentApi.patch('/portal/reseller/account/preferences', { offerRequestCourses: next })
      setOfferRequest(next)
      setSavedOffer(true)
      setTimeout(() => setSavedOffer(false), 2500)
    } catch (err) {
      setError(err?.response?.data?.message || err.message)
    } finally { setSavingOffer(false) }
  }

  return (
    <div style={{
      marginBottom: 24, padding: 16, background: '#fff',
      border: '1px solid #e2e8f0', borderRadius: 8, maxWidth: 760,
    }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 24 }}>
        <div style={{ flex: 1 }}>
          <h3 style={{ margin: '0 0 6px', fontSize: 14, color: '#0f172a', fontWeight: 600 }}>
            {t('sp_pref_provider_title', 'Preferred tee-sheet provider')}
          </h3>
          <p style={{ margin: '0 0 12px', fontSize: 13, color: '#64748b', lineHeight: 1.5 }}>
            {t('sp_pref_provider_blurb', "When a course supports both InteGolf and Apex, we'll use your preference by default. Courses on only one provider always use that one.")}
          </p>
          <div style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
            <select
              value={value}
              onChange={e => save(e.target.value)}
              disabled={loading || saving || !endpointReady}
              style={{ ...selectStyle, minWidth: 240, opacity: endpointReady ? 1 : 0.6 }}
              title={endpointReady ? '' : t('sp_pref_provider_coming_soon_tip', 'Coming soon — backend update is rolling out')}
            >
              {/* "InteGolf" and "Apex" are product names — left untranslated. */}
              <option value="">{t('sp_pref_provider_none', 'No preference (InteGolf, fall back to Apex)')}</option>
              <option value="integolf">InteGolf</option>
              <option value="apex">Apex</option>
            </select>
            {!loading && !endpointReady && (
              <span style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic' }}>
                {t('sp_pref_provider_pending', 'Backend update in progress — selection will be live shortly')}
              </span>
            )}
            {saving && <span style={{ fontSize: 12, color: '#64748b' }}>{t('sp_pref_provider_saving', 'Saving…')}</span>}
            {saved && !saving && <span style={{ fontSize: 12, color: '#15803d', fontWeight: 600 }}>{t('sp_pref_provider_saved', '✓ Saved')}</span>}
          </div>

          {/* Non-integration (request-mode) courses master switch — owner-only.
              When off, courses you don't book through a tee sheet are hidden
              from both your Stay & Play embed and the custom-trip builder. */}
          <div style={{ marginTop: 18, paddingTop: 16, borderTop: '1px solid #f1f5f9' }}>
            <label style={{ display: 'flex', gap: 10, alignItems: 'flex-start', cursor: (endpointReady && isOwner) ? 'pointer' : 'default' }}>
              <input
                type="checkbox"
                checked={offerRequest}
                disabled={loading || savingOffer || !endpointReady || !isOwner}
                onChange={e => saveOffer(e.target.checked)}
                style={{ marginTop: 2, width: 16, height: 16, cursor: (endpointReady && isOwner) ? 'pointer' : 'default' }}
              />
              <span style={{ flex: 1 }}>
                <span style={{ fontSize: 14, color: '#0f172a', fontWeight: 600 }}>{t('sp_offer_request_courses', 'Offer non-integration courses')}</span>
                <span style={{ display: 'block', fontSize: 13, color: '#64748b', marginTop: 3, lineHeight: 1.5 }}>
                  {t('sp_offer_request_courses_help', "Show courses you don't book through a tee sheet (the customer picks a time window and pays your price; you confirm the tee time by hand) on your Stay & Play and custom trips. Off hides them everywhere.")}
                </span>
              </span>
              {savingOffer && <span style={{ fontSize: 12, color: '#64748b' }}>{t('sp_pref_provider_saving', 'Saving…')}</span>}
              {savedOffer && !savingOffer && <span style={{ fontSize: 12, color: '#15803d', fontWeight: 600 }}>{t('sp_pref_provider_saved', '✓ Saved')}</span>}
            </label>
            {!isOwner && endpointReady && (
              <div style={{ fontSize: 12, color: '#94a3b8', fontStyle: 'italic', marginTop: 6, marginLeft: 26 }}>
                {t('sp_offer_request_owner_only', 'Only the account owner can change this.')}
              </div>
            )}
          </div>
        </div>
      </div>
      {error && (
        <div style={{ marginTop: 12, padding: 10, background: '#fef2f2', color: '#991b1b', border: '1px solid #fecaca', borderRadius: 6, fontSize: 13 }}>
          {error}
        </div>
      )}
    </div>
  )
}
